/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import 'styles/reset.css'
import 'styles/globals.css'
import React, { Suspense, useEffect, useRef } from 'react'
import Script from 'next/script'
import Head from 'next/head'
import TagManager from 'react-gtm-module'
import type { AppProps } from 'next/app'
import { ThemeProvider } from 'styled-components'
import { theme } from 'infra/theme'
import {
  CHANNEL_IO_PLUGIN_KEY,
  COOKIE_KEY_USER,
  FIRST_LANDING_PATH_KEY,
  FUNNEL_KEY,
  GOOGLE_TAG_MANAGER_ID,
  IS_APP,
  IS_BROWSER,
  IS_PRODUCTION,
  POSITION_FILTER_QUERY_PARAMS_KEY,
  ZIGHANG_USER_STORAGE_KEY,
} from 'infra/constants'
import { analytics } from 'infra/analytics'
import { SWRConfig } from 'swr'
import { StoresProvider } from 'stores/globals'
import { useRouter } from 'next/router'
import { LoginVerifier } from 'infra/login-verifier'
import { LoginModal } from 'components/misc/login'
import { Toaster } from 'react-hot-toast'
import { StartupModal } from 'components/startup/startup-modal'
import { PositionModal } from 'components/positions/position-modal'
import { ErrorBoundary } from 'components/misc/error-boundary'
import { PositionApplyModal } from 'components/positions/position-apply-modal'
import { ChannelService } from 'infra/channel'
import { commonToastStyle } from 'components/common/toast'
import { LocationSelectorModal } from 'components/misc/location-selector-modal'
import { useIsDesktop } from 'utils/hooks'
import { ProfileFillNeedModal } from 'components/scouts/request-form/profile/profile-fill-need-modal'
import { AppInstallPromptPopUp } from 'components/misc/app-install-prompt-pop-up'
import { PositionSelectorModal } from 'components/misc/position-selector-modal'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/ko'
import { DashboardPositionUpdateRequestModal } from 'components/dashboard/position-update-request-modal'
import { JobConditionModal } from 'components/job-condition-modal'
import { ServiceAreaUpdateRequestModal } from 'components/dashboard/service-area-update-request-modal'
import { postExternalPartnerZighangEvents } from 'apis/request'
import qs from 'query-string'
import {
  getFirstLandingPathStorage,
  getZighangUserStorage,
  setLocalStorage,
} from 'utils/storages'
import { useUser } from 'apis/hooks'
import { BanReasonNoticeModal } from 'components/dashboard/ban-reason-notice-modal'
import { RoutePaths } from 'types/router'
import { PositionAnalyzeModal } from 'components/positions/analysis/position-analyze-modal'
import { PositionAnalysisPushAlarm } from 'components/positions/analysis/position-analysis-push-alarm'
import { CreditAdModal } from 'components/dashboard/credit-ad-modal'
import { JobFairBannerModal } from 'components/dashboard/job-fair-banner-modal'
import { EmailConfirmModalContainer } from 'components/misc/email-confirm-modal'
import Cookies from 'js-cookie'
import { breadcrumbJsonLD } from 'infra/breadcrumb'

const channelService = new ChannelService()
dayjs.extend(localizedFormat)
dayjs.locale('ko')

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const isZighangInitialized = useRef(false)
  const isAnalyticsInitialized = useRef(false)
  const isDashboard = router.pathname.includes('dashboard')
  const { data: user } = useUser()
  if (IS_BROWSER) {
    if (
      !isZighangInitialized.current &&
      router.query.utm_source === 'zighang'
    ) {
      localStorage.setItem(
        POSITION_FILTER_QUERY_PARAMS_KEY,
        qs.stringify(router.query),
      ) // 직행에서 넘어온 필터 positionFilter에 적용
      localStorage.setItem(
        ZIGHANG_USER_STORAGE_KEY,
        JSON.stringify(router.query),
      ) // 직행 유저 정보 스토리지 저장
      isZighangInitialized.current = true
    }
    if (!isAnalyticsInitialized.current) {
      if (IS_PRODUCTION) {
        TagManager.initialize({ gtmId: GOOGLE_TAG_MANAGER_ID as string }) // NOTE(son) https://www.learnbestcoding.com/post/9/easiest-way-to-integrate-google-analytics-with-react-js-and-next-js
      }
      if (
        !router.pathname.includes('admin') &&
        !router.pathname.includes('pre-interviews') &&
        !router.pathname.includes('file-anonymization') &&
        router.pathname !== '/recommend' && // NOTE: 대시보드 매니저추천인재 페이지도 url에 recommend 포함하고 있어서 이렇게 비교
        router.pathname !== '/recommend/closed' &&
        isDesktop &&
        !IS_APP
      ) {
        channelService.boot({ pluginKey: CHANNEL_IO_PLUGIN_KEY })
      }
      analytics.init() // NOTE(son): useEffect로 실행 시 track을 읽을 수 없는 에러가 나타남, 그래서 initialClient로 중복호출 제거함
      isAnalyticsInitialized.current = true
    }
  }

  const funnel = router.query.funnel
  const content = (
    <Suspense>
      <div id='main'>
        <Suspense>
          <Component {...pageProps} />
        </Suspense>
      </div>
      {!isDashboard && (
        <Suspense>
          <StartupModal />
          <PositionModal />
          <LoginModal />
          <PositionApplyModal />
          <LocationSelectorModal />
          <PositionSelectorModal />
          {!router.pathname.includes('salary-analysis') &&
            !router.pathname.includes('request-form') &&
            !router.pathname.includes('landing') && <ProfileFillNeedModal />}
          <JobConditionModal />
          <PositionAnalyzeModal />
          <PositionAnalysisPushAlarm />
          <EmailConfirmModalContainer />
        </Suspense>
      )}
      {isDashboard &&
        !router.pathname.includes('login') &&
        !router.pathname.includes('waiting') && (
          <>
            <Suspense>
              <DashboardPositionUpdateRequestModal />
            </Suspense>
            <Suspense>
              <ServiceAreaUpdateRequestModal />
            </Suspense>
            <Suspense>
              <BanReasonNoticeModal />
            </Suspense>
            <JobFairBannerModal />
            <CreditAdModal />
          </>
        )}
      {!router.pathname.includes(RoutePaths.RESUME_ANALYZER) &&
        !router.pathname.includes(RoutePaths.PROFILE_EXAMPLES) &&
        !router.pathname.includes(RoutePaths.LANDING_STARTUP_SCOUT) &&
        !router.pathname.includes(RoutePaths.LANDING_RESUME) &&
        !router.pathname.includes('/login-callback') &&
        !router.pathname.includes('/salary-analysis') && (
          <AppInstallPromptPopUp />
        )}
      <Toaster
        toastOptions={{ style: commonToastStyle }}
        containerStyle={{ top: 32 }}
      />
    </Suspense>
  )

  useEffect(() => {
    // 직행에서 넘어올 때 트래픽 기록
    // 로그인 후 직행 유저임을 알아야해서 userId가 생기면 한번 더 트래픽 기록 후 직행 스토리지 제거
    const zighangStorage = getZighangUserStorage()
    if (zighangStorage) {
      postExternalPartnerZighangEvents({
        userId: user?.id || null,
        clientId: zighangStorage.client_id,
        targetUrl: user ? null : router.asPath.split('?')[0],
        zighangSourceUrl: user ? null : zighangStorage.zighang_source_url,
      })
      user && localStorage.removeItem(ZIGHANG_USER_STORAGE_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (funnel) localStorage.setItem(FUNNEL_KEY, JSON.stringify(funnel))
  }, [funnel])

  useEffect(() => {
    if (!IS_BROWSER || !!Cookies.get(COOKIE_KEY_USER)) return // 비회원일 때만 firstLandingPath 필요
    const firstLandingPath = getFirstLandingPathStorage()
    if (!firstLandingPath) {
      setLocalStorage(FIRST_LANDING_PATH_KEY, router.asPath.slice(0, 500))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Head>
        <title>그룹바이 | 스타트업 채용 플랫폼</title>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
        />
        <script
          id='breadcrumb-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLD) }}
        />
      </Head>
      <Script
        src='https://developers.kakao.com/sdk/js/kakao.min.js'
        strategy='beforeInteractive'
      />
      <Script src='/avoid-in-app.js' strategy='beforeInteractive' />
      <Script
        type='text/javascript'
        src='//wcs.naver.net/wcslog.js'
        strategy='afterInteractive'
      />
      <Script
        id='plain-javascript'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
          document.addEventListener('DOMContentLoaded', function() {
            var _nasa={}; //초기화구문
            window.wcs_add={}; // react native web view에서 에러가 발생해서 초기화구문 추가
            if (!window.wcs_add.wa) window.wcs_add = { wa: "s_511a07378f8b"};
            if (!_nasa) var _nasa={};
            if(window.wcs){
              wcs.inflow();
              wcs_do(_nasa);
              var _nasa={}; //초기화구문
            }
          });
          `,
        }}
      />
      <ThemeProvider theme={theme}>
        <ErrorBoundary>
          <SWRConfig value={{ suspense: true }}>
            <StoresProvider>
              {router.pathname.includes('/admin') ? (
                content
              ) : (
                <LoginVerifier>{content}</LoginVerifier>
              )}
            </StoresProvider>
          </SWRConfig>
        </ErrorBoundary>
      </ThemeProvider>
    </>
  )
}
